import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addFavoriteListing,
  removeFavoriteByRef,
  removeFavoriteListing,
} from "../../api/favoritesApi";
import { useAuth } from "../../auth/useAuth";
import { PENDING_FAVORITE_KEY } from "../../utils/savedSearchState";

const FavoriteButton = ({
  property,
  className = "",
  size = "sm",
  onFavoriteChange,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [favorited, setFavorited] = useState(Boolean(property?.favorited));
  const [favoriteListingId, setFavoriteListingId] = useState(property?.favoriteListingId || null);
  const [busy, setBusy] = useState(false);

  const listingId = property?.id || property?.canonicalListingId;

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!listingId) {
      return;
    }

    if (!isAuthenticated) {
      sessionStorage.setItem(
        PENDING_FAVORITE_KEY,
        JSON.stringify({
          canonicalListingId: listingId,
          returnTo: window.location.pathname + window.location.hash,
        })
      );
      navigate("/login", {
        state: { fromHash: window.location.hash || "#/" },
      });
      return;
    }

    try {
      setBusy(true);

      if (favorited) {
        if (favoriteListingId) {
          await removeFavoriteListing(favoriteListingId);
        } else {
          await removeFavoriteByRef({ canonicalListingId: listingId });
        }
        setFavorited(false);
        setFavoriteListingId(null);
        onFavoriteChange?.({ favorited: false, favoriteListingId: null });
      } else {
        const body = await addFavoriteListing({
          canonicalListingId: listingId,
          listingSnapshot: {
            title: property.title,
            price: property.priceMin ?? property.price,
            priceMax: property.priceMax,
            currency: property.currencyCode || "COP",
            listingMode: property.listingType || property.transactionType,
            addressLabel: [property.neighborhood, property.city || property.municipality]
              .filter(Boolean)
              .join(", "),
            bedrooms: property.bedrooms,
            bathrooms: property.bathrooms,
            imageUrl: property.imageUrl || property.primaryImageUrl,
          },
        });
        setFavorited(true);
        setFavoriteListingId(body.favorite?.favoriteListingId || null);
        onFavoriteChange?.({
          favorited: true,
          favoriteListingId: body.favorite?.favoriteListingId,
        });
      }
    } catch {
      // keep prior state on error
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`favorite-button favorite-button--${size}${favorited ? " is-favorited" : ""} ${className}`.trim()}
      onClick={handleClick}
      disabled={busy}
      aria-pressed={favorited}
      aria-label={favorited ? "Remove from favorites" : "Save to favorites"}
      title={favorited ? "Saved" : "Save"}
    >
      {favorited ? "♥" : "♡"}
    </button>
  );
};

export default FavoriteButton;
